import { useEffect, useState } from 'react';
import { ShieldWarning, ChatsCircle, Warning } from '@phosphor-icons/react';

import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { useNavigate, useParams } from '../lib/routing';
import { useIsAdminViewer } from '../hooks/use-is-admin-viewer';
import {
  CATEGORY_LABELS,
  SEVERITY_LABELS,
  SOURCE_LABELS,
  STATUS_LABELS,
  fetchAlert,
  fmtDateTime,
  setAlertStatus,
  type SafeguardingAlertDetail,
  type SafeguardingStatus,
} from '../lib/admin-safeguarding';

const HEADER_ICON = <ShieldWarning size={26} />;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
        {label}
      </span>
      <span className="text-[14px] text-[var(--naia-text-primary)]">{children}</span>
    </div>
  );
}

export default function AdminSafeguardingDetail() {
  const isAdminViewer = useIsAdminViewer();
  const params = useParams<{ id?: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();

  const [alert, setAlert] = useState<SafeguardingAlertDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetchAlert(id, 'open_alert')
      .then(setAlert)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  /* La consultation de la conversation est tracée SÉPARÉMENT de l'ouverture du
     dossier : lire un résumé et lire ce qu'un enfant a écrit ne sont pas le
     même acte, et la trace doit pouvoir les distinguer. */
  const openConversation = async () => {
    if (!alert?.threadId) return;
    await fetchAlert(id, 'open_conversation').catch(() => undefined);
    navigate(`/admin/conversations/${alert.threadId}`);
  };

  const changeStatus = async (status: SafeguardingStatus) => {
    await setAlertStatus(id, status);
    setAlert((a) => (a ? { ...a, status } : a));
  };

  const danger = alert?.severity === 'person_at_risk';

  return (
    <>
      <Header />
      <main className="mx-auto max-w-[900px] px-5 pb-16 pt-2">
        <HeaderContent>
          <span className={HEADER_BADGE_CLASS}>{HEADER_ICON}</span>
          <h1 className={HEADER_TITLE_CLASS}>Dossier d&apos;alerte</h1>
        </HeaderContent>

        <Button variant="ghost" size="sm" className="mt-2" onClick={() => navigate('/admin/safeguarding')}>
          ← Toutes les alertes
        </Button>

        {loading && <p className="mt-6 text-[var(--naia-text-secondary)]">Chargement…</p>}
        {error && (
          <div className="mt-4 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-error-bg)] p-4 text-[13px] text-[var(--naia-error)]">
            {error}
          </div>
        )}
        {!loading && !error && !alert && (
          <p className="mt-6 text-[var(--naia-text-secondary)]">Dossier introuvable.</p>
        )}

        {alert && (
          <>
            {danger && (
              <div className="mt-4 flex items-start gap-3 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-error-bg)] p-4">
                <Warning size={22} weight="fill" className="mt-0.5 shrink-0 text-[var(--naia-error)]" />
                <div className="text-[13px] leading-relaxed text-[var(--naia-text-primary)]">
                  <strong className="text-[var(--naia-error)]">Personne en danger.</strong> Le dispositif ne
                  contacte aucun service extérieur et ne prévient pas les responsables légaux. Si le danger
                  peut venir du foyer, ne prévenez pas le compte parent : suivez la conduite à tenir écrite.
                </div>
              </div>
            )}

            <div className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-5 shadow-[var(--naia-shadow-card)]">
              <Field label="Gravité">{SEVERITY_LABELS[alert.severity]?.label ?? alert.severity}</Field>
              <Field label="Nature">{CATEGORY_LABELS[alert.category] ?? alert.category}</Field>
              <Field label="Détecté le">{fmtDateTime(alert.detectedAt)}</Field>
              <Field label="Origine">{SOURCE_LABELS[alert.source] ?? alert.source}</Field>
              <Field label="Notification">
                {alert.notifiedAt ? (
                  fmtDateTime(alert.notifiedAt)
                ) : danger ? (
                  <span className="font-bold text-[var(--naia-error)]">non partie</span>
                ) : (
                  'sans objet (risque légal)'
                )}
              </Field>
              <Field label="Surface">{alert.surface ?? '—'}</Field>
              <Field label="Compte">{alert.userEmail ?? '—'}</Field>
              <Field label="Élève concerné">{alert.studentFirstName ?? '—'}</Field>
              <Field label="Modèle">
                {alert.modelMissed ? "n'a pas appelé l'outil" : "a appelé l'outil"}
              </Field>
            </div>

            <section className="mt-5 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-5 shadow-[var(--naia-shadow-card)]">
              <h2 className="text-[13px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
                Résumé
              </h2>
              {alert.summary ? (
                <p className="mt-2 whitespace-pre-wrap text-[14px] leading-relaxed text-[var(--naia-text-primary)]">
                  {alert.summary}
                </p>
              ) : (
                <p className="mt-2 text-[13px] leading-relaxed text-[var(--naia-text-secondary)]">
                  Aucun résumé : cette alerte vient du contrôle serveur, qui détecte sans qualifier. Ouvrez la
                  conversation pour juger. Signaux déclenchés :{' '}
                  <code className="rounded bg-[var(--naia-info-bg)] px-1 py-0.5 text-[12px]">
                    {alert.signals.length > 0 ? alert.signals.join(', ') : '—'}
                  </code>
                </p>
              )}
            </section>

            <div className="mt-5 flex flex-wrap items-end gap-3">
              <Button onClick={openConversation} disabled={!alert.threadId}>
                <ChatsCircle size={16} /> Ouvrir la conversation
              </Button>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
                  État du dossier
                </span>
                {isAdminViewer ? (
                  <NativeSelect
                    value={alert.status}
                    onChange={(e) => void changeStatus(e.target.value as SafeguardingStatus)}
                  >
                    {Object.entries(STATUS_LABELS).map(([v, label]) => (
                      <option key={v} value={v}>
                        {label}
                      </option>
                    ))}
                  </NativeSelect>
                ) : (
                  <span className="text-[14px] text-[var(--naia-text-primary)]">
                    {STATUS_LABELS[alert.status]}
                  </span>
                )}
              </label>
            </div>
            {!alert.threadId && (
              <p className="mt-2 text-[12px] text-[var(--naia-text-secondary)]">
                La conversation a été effacée. Le dossier subsiste, sans son contexte.
              </p>
            )}

            <section className="mt-6 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-5 shadow-[var(--naia-shadow-card)]">
              <h2 className="text-[13px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
                Qui a consulté ce dossier
              </h2>
              {alert.reads.length === 0 ? (
                <p className="mt-2 text-[13px] text-[var(--naia-text-secondary)]">Aucune consultation antérieure.</p>
              ) : (
                <ul className="mt-2 flex flex-col gap-1.5">
                  {alert.reads.map((r, i) => (
                    <li key={`${r.readAt}-${i}`} className="text-[13px] text-[var(--naia-text-primary)]">
                      <span className="font-semibold">{r.readerEmail ?? 'compte supprimé'}</span>
                      <span className="text-[var(--naia-text-secondary)]">
                        {' — '}
                        {r.action === 'open_conversation' ? 'a lu la conversation' : 'a ouvert le dossier'}
                        {' — '}
                        {fmtDateTime(r.readAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </>
  );
}
